import { CopyResourceResultDc, ProjectContentItemDc, ServiceInfoDc } from "@evergis/api";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  isCopyCurrentProject as isCopyCurrentProjectSelector,
  isCopyLayerFromProject as isCopyLayerFromProjectSelector,
} from "../../../../ducks/dialogs/selectors";

import { resetSelected } from "../../../../ducks/catalog/catalog";
import { resetDialog } from "../../../../ducks/dialogs/dialogs";
import { useNotification } from "../../../../hooks/notifications/useNotification";
import { useCurrentProjectWithAddedLayers } from "../../../../hooks/project/useAddLayersToCurrentProject";
import { useOpenProject } from "../../../../hooks/project/useOpenProject";
import { useProgress } from "../../../../hooks/scheduler/useProgress";
import { useProjectInfo, useProjectUpdate } from "../../../../ducks/project/hooks";
import { pipelineTask } from "../../../../resources/scheduler";
import { useCopyResults } from "./useCopyResults";
import { useProjectInfoByName } from "../../../../hooks/project/useProjectByName";
import { createFilterCopyTask, FilterCopyTask } from "../../../../evergis/core/tools/createFilterCopyTask";
import { createPipelineTask } from "../../../../evergis/core/tools/createPipelineTask";
import { layers } from "../../../../resources/layers";
import { useDialogInfo } from "../../../../hooks/useDialogs";

import { TaskDataType } from "../../../../interfaces/scheduler";
import { CopyJobNotification } from "./useCopyJobNotifications";

interface AfterBulkCopyProps {
  jobResult: CopyResourceResultDc[];
  jobLoading: boolean;
  jobError: Error;
}

interface AfterBulkCopyResponse {
  setInnerTasks: (state: FilterCopyTask<TaskDataType.Table>[]) => void;
}

// eslint-disable-next-line max-lines-per-function
export const useAfterCopy = (
  notificationInfo: CopyJobNotification,
  props: AfterBulkCopyProps,
  filteredLayers?: ServiceInfoDc[],
  isCopySharedProject = false,
  copyTables = false,
  showNotifications = true,
): AfterBulkCopyResponse => {
  const { jobResult, jobLoading, jobError } = props;
  const [pipeline, { data }] = pipelineTask.use();
  const [innerTasks, setInnerTasks] = useState<FilterCopyTask<TaskDataType.Table>[]>([]);
  const [currentProjectInfo] = useProjectInfo();
  const { items: projectItems } = currentProjectInfo.content || {};
  const { successNotification, partialSuccessNotification, errorNotification } = notificationInfo;
  const [pipelineStarted, setStarted] = useState(false);
  const { isTaskCompleted, isTaskFailed } = useProgress(data?.taskId);
  const pipelineCompleted = copyTables ? isTaskCompleted : true;
  const allJobsSucceeded: boolean = jobResult && jobResult.every(item => item.isSuccess) && pipelineCompleted;
  const allJobsFailed: boolean = (jobResult && jobResult.every(item => !item.isSuccess)) || isTaskFailed;
  const someJobsFailed: boolean =
    jobResult && jobResult.some(item => !item.isSuccess) && jobResult.some(item => item.isSuccess);

  const { filteredLayersTables, layersToAddToProject, filteredLayerInfos, projectsCopied, layersCopied, tablesCopied } =
    useCopyResults(jobResult, filteredLayers);

  const createdProjectName: string = useMemo(() => {
    return projectsCopied.length && projectsCopied[0].copyName;
  }, [projectsCopied]);

  const projectContentItemsToBeReplaced: ProjectContentItemDc[] = projectItems
    ? projectItems.map((item: ProjectContentItemDc) => {
        const copiedLayer: CopyResourceResultDc | undefined = layersCopied.find(value => value.name === item.name);

        if (copiedLayer) {
          return {
            ...item,
            name: copiedLayer.copyName,
          };
        }

        return item;
      })
    : [];

  const addCopiedLayersToCurrentProject = useCurrentProjectWithAddedLayers(layersToAddToProject as ServiceInfoDc[]);
  const isCopyCurrentProject = useSelector(isCopyCurrentProjectSelector);
  const isCopyLayerFromProject: boolean = useSelector(isCopyLayerFromProjectSelector);
  const openProject = useOpenProject(createdProjectName);
  const [createdProjectInfo] = useProjectInfoByName(createdProjectName);
  const updateProject = useProjectUpdate();
  const { options: dialogOptions } = useDialogInfo();
  const dispatch = useDispatch();

  useEffect(() => {
    if (jobResult && !jobLoading && !jobError && innerTasks) {
      const copyFilteredLayersTasks: FilterCopyTask<TaskDataType.Layer>[] = layersCopied.reduce(
        (acc: FilterCopyTask<TaskDataType.Layer>[], curr: CopyResourceResultDc) => {
          const layerToCopy = filteredLayerInfos.find(layer => layer.name === curr.name);

          if (!layerToCopy) {
            return acc;
          }

          return [
            ...acc,
            createFilterCopyTask({
              dataStorage: TaskDataType.Layer,
              sourceName: curr.name,
              targetName: curr.copyName,
              condition: layerToCopy.condition,
            }),
          ];
        },
        [],
      );

      const copyTableDataTasks: FilterCopyTask<TaskDataType.Table>[] = innerTasks.reduce(
        (acc: FilterCopyTask<TaskDataType.Table>[], curr: FilterCopyTask<TaskDataType.Table>) => {
          const currentTableName: string = curr.source.tableName;
          const isTableCopied: boolean = tablesCopied.map(item => item.name).includes(currentTableName);
          const isTableLayerFiltered: boolean = filteredLayersTables.includes(currentTableName);

          if (isTableCopied && !isTableLayerFiltered) {
            return [...acc, curr];
          }

          return acc;
        },
        [],
      );

      if (copyFilteredLayersTasks.length) {
        pipeline({ input: createPipelineTask(copyFilteredLayersTasks) });
      }

      if (copyTableDataTasks.length && !pipelineStarted) {
        setStarted(true);
        pipeline({ input: createPipelineTask(copyTableDataTasks) });
      }

      if (isCopyLayerFromProject || dialogOptions?.fromTable || dialogOptions?.fromFeatureCard) {
        addCopiedLayersToCurrentProject();
      }

      if (isCopySharedProject && createdProjectInfo) {
        updateProject({
          ...createdProjectInfo,
          name: createdProjectName,
          content: {
            ...currentProjectInfo.content,
            items: projectContentItemsToBeReplaced,
          },
        });

        openProject();
      }

      if (isCopyCurrentProject && !isCopySharedProject) {
        openProject();
      }

      if (pipelineCompleted) {
        dispatch(resetSelected());
        dispatch(resetDialog());
        layers.invalidateAll();
      }
    }
  }, [
    jobResult,
    jobLoading,
    jobError,
    innerTasks,
    isCopyCurrentProject,
    isCopyLayerFromProject,
    filteredLayersTables,
    projectsCopied,
    layersCopied,
    tablesCopied,
    addCopiedLayersToCurrentProject,
    isCopySharedProject,
    createdProjectName,
    createdProjectInfo,
    projectContentItemsToBeReplaced,
    filteredLayerInfos,
    pipelineStarted,
    pipelineCompleted,
    dialogOptions?.fromTable,
    dialogOptions?.fromFeatureCard,
  ]);

  useNotification(allJobsSucceeded && !jobLoading && !jobError, successNotification, { skip: !showNotifications });
  useNotification(allJobsFailed || jobError, errorNotification, { skip: !showNotifications });
  useNotification(someJobsFailed || jobError, partialSuccessNotification, { skip: !showNotifications });

  return { setInnerTasks };
};
